import React, { useState, useEffect } from 'react';
import { FaGithub, FaGlobe } from 'react-icons/fa';
import LazyVideo from './LazyVideo';
import { ProjectSkeleton } from '../ui/SkeletonLoader';
import GitHubStars from '../ui/GitHubStars';
import ZaikaFlowVideo from '../../assets/optimized/Zaikaflowad_compressed.mp4';
import TestFlowAIVideo from '../../assets/optimized/testflowai_compressed.mp4';
import OfferWhiskyVideo from '../../assets/optimized/OfferWhisky_compressed.mp4';
import BlobTrackingVideo from '../../assets/blobTracking.mp4'; // This one is smaller in original

const ProjectCard = ({ project }) => {
    const { title, description, date, technologies, github, demo, gradient, textColor, video, showStars } = project;
    
    return (
        <div className={`relative rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 ${gradient}`}>
            {/* Project interface with video or placeholder */}
            <div className="mb-6 bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden">
                {video ? (
                    <LazyVideo
                        src={video}
                        className="w-full rounded-lg"
                        height={250}
                        loop={true}
                        muted={true}
                        playsInline={true}
                    />
                ) : (
                    <div className="p-4 min-h-[200px] flex items-center justify-center">
                        <div className="text-center">
                            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                                <FaGlobe size={24} className="text-white" />
                            </div>
                            <div className="text-white/80 text-sm">{title}</div>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Project info */}
            <div className="space-y-4">
                <div>
                    <h3 className={`text-xl font-bold ${textColor} mb-2`}>{title}</h3>
                    <p className="text-sm text-gray-400 mb-1">{date}</p>
                    <p className={`text-sm ${textColor} opacity-90 leading-relaxed`}>{description}</p>
                </div>
                
                {/* Technologies */}
                <div className="flex flex-wrap gap-2">
                    {technologies.map((tech, index) => (
                        <span 
                            key={index}
                            className="px-2 py-1 text-xs bg-white/20 backdrop-blur-sm rounded-md text-white/90"
                        >
                            {tech}
                        </span>
                    ))}
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-2">
                    {demo && (
                        <a 
                            href={demo} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-white text-sm hover:bg-white/30 transition-colors no-underline"
                        >
                            <FaGlobe size={16} />
                            Website
                        </a>
                    )}
                    {github && (
                        <div className="flex items-center gap-2">
                            <a 
                                href={github} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-white text-sm hover:bg-white/30 transition-colors no-underline"
                            >
                                <FaGithub size={16} />
                                Source
                            </a>
                            {/* Show GitHub stars for projects with showStars enabled */}
                            {showStars && (
                                <GitHubStars repoUrl={github} className="ml-1" />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Projects = () => {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Simulate initial loading time
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500);

        return () => clearTimeout(timer);
    }, []);
    const projects = [
        {
            title: 'Blob Tracking',
            date: 'Dec 2024 - Present',
            description: 'A cutting-edge real-time blob tracking system built with TouchDesigner for creating interactive and generative visual experiences. Features advanced computer vision for installations, motion capture, and generative art applications.',
            technologies: ['TouchDesigner', 'Computer Vision', 'OpenCV', 'Real-time Processing'],
            github: 'https://github.com/DevxD98/Blobtracking',
            video: BlobTrackingVideo,
            gradient: 'bg-gradient-to-br from-gray-900 via-black to-blue-900',
            textColor: 'text-white',
            showStars: true
        },
        {
            title: 'TestFlowAI',
            date: 'Oct 2025 - Present',
            description: 'An intelligent web testing platform that blends heuristic generation with AI assistance. Features real-time execution monitoring, evidence capture, comprehensive reporting with coverage analysis, and AI-powered test case generation.',
            technologies: ['Next.js', 'TypeScript', 'AI Integration', 'Playwright', 'Node.js'],
            github: 'https://github.com/DevxD98/auto-tester',
            video: TestFlowAIVideo,
            gradient: 'bg-gradient-to-br from-gray-900 via-black to-blue-900',
            textColor: 'text-white'
        },
        {
            title: 'ZaikaFlow',
            date: 'Jan 2024 - Feb 2024',
            description: 'An innovative platform designed to streamline and automate food delivery services, enhancing efficiency and customer satisfaction.',
            technologies: ['JavaScript', 'TypeScript','Node.js'],
            demo: 'https://zaika-flow.vercel.app',
            video: ZaikaFlowVideo,
            gradient: 'bg-gradient-to-br from-gray-900 via-black to-blue-900',
            textColor: 'text-white'
        },
        {
            title: 'OfferWhisky',
            date: 'June 2025 - August 2025',
            description: 'Designed, developed and sold animated UI for shop-owners as an intern in DEC Gaming Studio.',
            technologies: ['React'],
            demo: 'https://owner-s-page.vercel.app/',
            video: OfferWhiskyVideo,
            gradient: 'bg-gradient-to-br from-gray-900 via-black to-blue-900',
            textColor: 'text-white'
        },
        
    ];

    return (
        <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                        Check out my latest work
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        I've worked on a variety of projects, from simple websites to complex web applications. Here are a few of my favorites.
                    </p>
                </div>

                {/* Projects Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                    {isLoading
                        ? Array(4).fill().map((_, index) => (
                            <ProjectSkeleton key={index} />
                          ))
                        : projects.map((project, index) => (
                            <ProjectCard key={index} project={project} />
                          ))
                    }
                </div>
            </div>
        </div>
    );
};

export default Projects;
